// Keeps the theme's out-of-drawer cart UI in sync with Rebuy Smart Cart (rebuy mode only).
//
// Rebuy owns the drawer, but the theme still renders the header count bubble
// (#cart-icon-bubble .cart-count-bubble) and has cart-aware widgets that listen on the theme's
// cart channels — frequently-bought.js, rebuy-bundle-validator.js, free-gift-validator.js, etc.
// In Dawn mode assets/cart.js drove those; that file isn't loaded under rebuy.
//
// Rebuy fires `rebuy:cart.change` for EVERY mutation from ANY source — native PDP add, drawer
// upsell, qty change, removal (see developers.rebuyengine.com/reference/cart-event-listeners). We
// use it as the trigger, then read the count from /cart.js. NOTE: Rebuy's own cart copy
// (`window.Rebuy.Cart.cart`) and the event's `detail.cart.cart` snapshot LAG — the first change
// event observed fires with a stale item_count (0 right after an add), so we must NOT trust them.
// /cart.js is authoritative and already fresh by the time the event fires (Rebuy mutates via the
// Shopify cart API). One fetch, correct count — no stale flicker.

// Update the header count bubble to match `itemCount`. Mirrors snippets/cart-icon-bubble.liquid:
// the bubble div exists only when the cart is non-empty, and the visible number shows only < 100.
export const updateCartBubble = (itemCount) => {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    const count = Number(itemCount) || 0;
    let bubble = cartLink.querySelector('.cart-count-bubble');

    if (count === 0) {
        if (bubble) bubble.remove();
        return;
    }
    if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        cartLink.appendChild(bubble);
    }
    const visible = count < 100 ? `<span aria-hidden="true">${count}</span>` : '';
    bubble.innerHTML = `${visible}<span class="visually-hidden">${count} item${count === 1 ? '' : 's'}</span>`;
};

// Rebroadcast on the theme's two cart channels so cart-aware widgets re-read state. Payload shapes
// match assets/cart.js exactly: pubsub gets a {source} marker, the DOM event carries the full cart.
const broadcastCartUpdate = (cart) => {
    if (typeof window.publish === 'function') {
        window.publish('cart-update', { source: 'rebuy-smart-cart' });
    }
    document.dispatchEvent(
        new CustomEvent('cart-data-updated', { bubbles: true, detail: { cartData: cart } })
    );
};

// Reconcile bubble + widgets from the authoritative Shopify cart. Exported so the compat shim can
// force a sync after a native add.
export const syncCartUI = async () => {
    try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        updateCartBubble(cart.item_count);
        broadcastCartUpdate(cart);
    } catch (error) {
        console.error('Rebuy cart sync failed:', error);
    }
};

// Debounce coalesces the burst of events Rebuy fires per change (add + change + show together) into
// a single /cart.js read.
let debounceTimer = null;
const scheduleSync = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        debounceTimer = null;
        syncCartUI();
    }, 100);
};

export const initRebuyCartSync = () => {
    // `rebuy:cart.change` is the canonical superset signal (add/remove/qty, any source); `cart.ready`
    // seeds the initial state. We ignore the event payload (its cart snapshot lags) and read the
    // authoritative count from /cart.js in syncCartUI.
    document.addEventListener('rebuy:cart.change', scheduleSync);
    document.addEventListener('rebuy:cart.ready', scheduleSync);

    // bfcache restore re-shows a frozen page with a stale bubble — reconcile from /cart.js.
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) syncCartUI();
    });

    syncCartUI();
};

export default initRebuyCartSync;
